import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronRight,
  Upload,
  FileText,
  ImageIcon,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { API_BASE, formatApiError } from "@/lib/api";
import { GENDER_OPTIONS, ENGLISH_LEVELS } from "@/lib/status";
import { JobCategoryCombobox } from "@/components/JobCategoryCombobox";
import { toast } from "sonner";

const PHONE_RE = /^(?:\+?91[\s-]?)?[6-9]\d{9}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SimpleForm({ termsAcceptedAt, termsLang }) {
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [d, setD] = useState({
    full_name: "",
    phone: "",
    whatsapp: "",
    email: "",
    gender: "",
    age: "",
    current_location: "",
    current_location_other: "",
    current_area: "",
    job_category: "",
    job_category_other: "",
    industry_preference: "",
    expected_salary: "",
    english_level: "",
  });
  const [cvFile, setCvFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    axios.get(`${API_BASE}/meta/job-categories`).then((r) => setCategories(r.data));
    axios
      .get(`${API_BASE}/meta/job-category-groups`)
      .then((r) => setCategoryGroups(r.data))
      .catch(() => {});
    axios.get(`${API_BASE}/meta/industries`).then((r) => setIndustries(r.data));
    axios
      .get(`${API_BASE}/meta/areas`)
      .then((r) => setCities(r.data.cities_with_areas || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!d.current_location || d.current_location === "Other") {
      setAreas([]);
      return;
    }
    axios
      .get(`${API_BASE}/meta/areas`, { params: { city: d.current_location } })
      .then((r) => setAreas(r.data.areas || []))
      .catch(() => setAreas([]));
  }, [d.current_location]);

  const err = (k) => {
    const v = (d[k] ?? "").toString().trim();
    if (k === "full_name") {
      if (!v) return "Required";
      if (v.length < 3) return "Too short";
    }
    if (k === "phone") {
      if (!v) return "Required";
      if (!PHONE_RE.test(v.replace(/\s+/g, "")))
        return "Enter valid 10-digit Indian mobile";
    }
    if (k === "whatsapp" && v && !PHONE_RE.test(v.replace(/\s+/g, "")))
      return "Enter valid 10-digit mobile";
    if (k === "email" && v && !EMAIL_RE.test(v)) return "Invalid email";
    if (k === "age" && v) {
      const n = parseInt(v, 10);
      if (Number.isNaN(n) || n < 16 || n > 70) return "16–70";
    }
    return null;
  };

  const OTHER_JOB = "Other (please specify)";

  const canSubmit = () => {
    const cityOk =
      d.current_location &&
      (d.current_location !== "Other" ||
        (d.current_location_other && d.current_location_other.trim()));
    const jobOk =
      d.job_category &&
      (d.job_category !== OTHER_JOB ||
        (d.job_category_other && d.job_category_other.trim()));
    return (
      d.full_name &&
      d.phone &&
      d.gender &&
      jobOk &&
      cityOk &&
      cvFile &&
      !err("full_name") &&
      !err("phone") &&
      !err("whatsapp") &&
      !err("email") &&
      !err("age")
    );
  };

  const upload = async (file, purpose) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("purpose", purpose);
    const { data: r } = await axios.post(`${API_BASE}/upload`, fd);
    return r.path;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const cv_path = await upload(cvFile, "cv");
      const photo_path = photoFile ? await upload(photoFile, "photo") : null;
      const finalCity =
        d.current_location === "Other"
          ? d.current_location_other.trim()
          : d.current_location;
      const payload = {
        full_name: d.full_name,
        phone: d.phone,
        whatsapp: d.whatsapp || null,
        email: d.email?.trim() || null,
        gender: d.gender || null,
        age: d.age ? parseInt(d.age, 10) : null,
        current_location: finalCity,
        current_area: d.current_area || null,
        job_category:
          d.job_category === OTHER_JOB
            ? d.job_category_other.trim()
            : d.job_category,
        industry_preference: d.industry_preference || null,
        expected_salary: d.expected_salary ? Number(d.expected_salary) : null,
        english_level: d.english_level || null,
        cv_path,
        photo_path,
        has_cv: true,
        resume_built: false,
        terms_accepted_at: termsAcceptedAt || new Date().toISOString(),
        terms_lang: termsLang || "en",
      };
      const { data: created } = await axios.post(
        `${API_BASE}/candidates`,
        payload
      );

      // Ack PDF (non-blocking)
      try {
        const { generateTermsAckPDF } = await import("@/lib/terms-ack");
        const { blob } = generateTermsAckPDF({
          id: created.id,
          full_name: payload.full_name,
          phone: payload.phone,
          email: payload.email,
          job_category: payload.job_category,
          terms_accepted_at: payload.terms_accepted_at,
        });
        const ackFile = new File(
          [blob],
          `tnc-ack-${payload.full_name.replace(/\s+/g, "_")}.pdf`,
          { type: "application/pdf" }
        );
        const fd = new FormData();
        fd.append("file", ackFile);
        fd.append("purpose", "resume");
        const { data: up } = await axios.post(`${API_BASE}/upload`, fd);
        await axios.patch(
          `${API_BASE}/candidates/public/${created.id}?token=${created.edit_token}`,
          { terms_acknowledgement_path: up.path }
        );
      } catch {
        // ignore ack failure
      }

      if (created.returning_candidate) {
        toast.success(
          `Welcome back, ${payload.full_name}! Profile updated (submission #${created.submission_count}).`
        );
      }
      nav("/submitted");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fbfdfc]">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Logo size={42} />
          <div className="text-xs text-ink-600 hidden sm:block">
            Quick candidate registration (with CV)
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-6 md:py-10">
        <div className="mb-6 animate-fade-in-up">
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-ink-900">
            Just a few details
          </h1>
          <p className="text-sm text-ink-600 mt-1">
            Since you have a CV, we only need the essentials. Attach your CV
            (mandatory) and a photo (optional) at the end.
          </p>
        </div>

        <Card className="tj-card">
          <CardContent className="p-5 md:p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Full Name *" error={err("full_name")}>
                <Input
                  data-testid="sf-full-name"
                  value={d.full_name}
                  onChange={(e) => set("full_name", e.target.value)}
                />
              </F>
              <F label="Phone Number *" error={err("phone")}>
                <Input
                  data-testid="sf-phone"
                  inputMode="tel"
                  maxLength={13}
                  value={d.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="10-digit Indian mobile"
                />
              </F>
              <F label="WhatsApp (if different)" error={err("whatsapp")}>
                <Input
                  data-testid="sf-whatsapp"
                  inputMode="tel"
                  maxLength={13}
                  value={d.whatsapp}
                  onChange={(e) => set("whatsapp", e.target.value)}
                />
              </F>
              <F label="Email" error={err("email")}>
                <Input
                  data-testid="sf-email"
                  type="email"
                  value={d.email}
                  onChange={(e) => set("email", e.target.value)}
                />
              </F>
              <F label="Gender *">
                <Select
                  value={d.gender}
                  onValueChange={(v) => set("gender", v)}
                >
                  <SelectTrigger data-testid="sf-gender">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDER_OPTIONS.map((g) => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
              <F label="Age" error={err("age")}>
                <Input
                  data-testid="sf-age"
                  type="number"
                  min="16"
                  max="70"
                  value={d.age}
                  onChange={(e) => set("age", e.target.value)}
                />
              </F>
              <F label="Current City *">
                <Select
                  value={d.current_location}
                  onValueChange={(v) =>
                    set("current_location", v) || set("current_area", "")
                  }
                >
                  <SelectTrigger data-testid="sf-city">
                    <SelectValue placeholder="Select your city" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                    <SelectItem value="Other">Other (type below)</SelectItem>
                  </SelectContent>
                </Select>
              </F>
              {d.current_location === "Other" && (
                <F label="Type your city">
                  <Input
                    data-testid="sf-city-other"
                    value={d.current_location_other}
                    onChange={(e) =>
                      set("current_location_other", e.target.value)
                    }
                  />
                </F>
              )}
              {areas.length > 0 && (
                <F label={`Area in ${d.current_location}`} full>
                  <Select
                    value={d.current_area}
                    onValueChange={(v) => set("current_area", v)}
                  >
                    <SelectTrigger data-testid="sf-area">
                      <SelectValue placeholder="Select your area" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {areas.map((a) => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
              )}
              <F label="Job Category *" full>
                <JobCategoryCombobox
                  value={d.job_category}
                  onChange={(v) => set("job_category", v)}
                  groups={categoryGroups}
                  items={categories}
                  testid="sf-job"
                />
              </F>
              {d.job_category === OTHER_JOB && (
                <F label="Please specify your role *" full>
                  <Input
                    data-testid="sf-job-other"
                    value={d.job_category_other}
                    onChange={(e) => set("job_category_other", e.target.value)}
                    placeholder="e.g. Drone Pilot, Yoga Instructor"
                  />
                </F>
              )}
              <F label="Industry" full>
                <Select
                  value={d.industry_preference}
                  onValueChange={(v) => set("industry_preference", v)}
                >
                  <SelectTrigger data-testid="sf-industry">
                    <SelectValue placeholder="Preferred industry (optional)" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {industries.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
              <F label="Expected Salary (₹ / month)">
                <Input
                  data-testid="sf-salary"
                  type="number"
                  step="500"
                  value={d.expected_salary}
                  onChange={(e) => set("expected_salary", e.target.value)}
                  placeholder="e.g. 15000"
                />
              </F>
              <F label="English Level">
                <Select
                  value={d.english_level}
                  onValueChange={(v) => set("english_level", v)}
                >
                  <SelectTrigger data-testid="sf-english">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENGLISH_LEVELS.map((l) => (
                      <SelectItem key={l.value} value={l.value}>
                        {l.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <FileDrop
                label="Upload CV * (PDF / DOC / DOCX)"
                accept=".pdf,.doc,.docx"
                icon={<FileText size={18} />}
                file={cvFile}
                onChange={setCvFile}
                testid="sf-upload-cv"
              />
              <FileDrop
                label="Profile Photo (optional)"
                accept="image/*"
                icon={<ImageIcon size={18} />}
                file={photoFile}
                onChange={setPhotoFile}
                testid="sf-upload-photo"
              />
            </div>

            <div className="text-xs text-ink-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
              Your details will be used by Tulsi Jobs Placement Consultant only
              to match you with suitable openings. Your CV will be shared with
              prospective employers.
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end mt-6">
          <Button
            data-testid="sf-submit-btn"
            disabled={submitting || !canSubmit()}
            onClick={submit}
            className="bg-brand-700 hover:bg-brand-800 text-white"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" /> Submitting…
              </>
            ) : (
              <>
                Submit Application <CheckCircle2 size={16} className="ml-2" />
              </>
            )}
          </Button>
        </div>
      </main>
      <footer className="text-center text-xs text-ink-600 py-8 space-x-3">
        <span>© {new Date().getFullYear()} Tulsi Jobs Placement Consultant</span>
        <span className="text-ink-400">·</span>
        <a href="/terms" className="text-brand-700 hover:underline">Terms</a>
        <span className="text-ink-400">·</span>
        <a href="/refund-policy" className="text-brand-700 hover:underline">Refund Policy</a>
      </footer>
    </div>
  );
}

function F({ label, error, full, children }) {
  return (
    <div className={full ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
      <Label className="text-xs uppercase tracking-[0.14em] text-ink-600 font-medium">
        {label}
      </Label>
      {children}
      {error && (
        <p className="text-[11px] text-rose-700 mt-0.5" data-testid="sf-field-error">
          {error}
        </p>
      )}
    </div>
  );
}

function FileDrop({ label, accept, icon, file, onChange, testid }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-ink-800">{label}</Label>
      <label
        data-testid={testid}
        className="flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-slate-300 cursor-pointer hover:border-brand-400 hover:bg-brand-50/40 transition-colors"
      >
        <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 grid place-items-center">
          {icon || <Upload size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-ink-900 truncate">
            {file ? file.name : "Click to choose a file"}
          </div>
          <div className="text-xs text-ink-600">
            {file
              ? `${(file.size / 1024).toFixed(0)} KB · ${file.type || "file"}`
              : "Max 10 MB"}
          </div>
        </div>
        <input
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => onChange(e.target.files?.[0] || null)}
        />
      </label>
    </div>
  );
}
